import React, { useMemo, useState } from "react";
import AddressInput, { AddressData } from "../../AddressInput";
import type { VenueData, VenueHistoryItem } from "../../../types/venue";

type VenueInfoProps = {
	venueData: VenueData;
	onChange: (venueData: VenueData) => void;
	required?: boolean;
	venueHistory?: VenueHistoryItem[];
	showHistorySelector?: boolean;
};

const DEFAULT_OPTION_VALUE = "__new__";
const createEmptyVenueData = (): VenueData => ({
	venueName: "",
	postalCode: "",
	prefecture: "",
	city: "",
	address: "",
});

const VenueInfo: React.FC<VenueInfoProps> = ({
	venueData,
	onChange,
	required = false,
	venueHistory = [],
	showHistorySelector = true,
}) => {
	const [selectedHistoryId, setSelectedHistoryId] =
		useState<string>(DEFAULT_OPTION_VALUE);

	const buildNextState = (partial: Partial<VenueData>) => ({
		...venueData,
		...partial,
	});

	const handleManualChange = (nextState: VenueData) => {
		setSelectedHistoryId(DEFAULT_OPTION_VALUE);
		onChange(nextState);
	};

	const handleVenueNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		handleManualChange(
			buildNextState({
				venueName: e.target.value,
			})
		);
	};

	const handleAddressChange = (addressData: AddressData) => {
		const fullAddress = addressData.buildingName
			? `${addressData.streetAddress} ${addressData.buildingName}`
			: addressData.streetAddress;

		handleManualChange(
			buildNextState({
				postalCode: addressData.postalCode,
				prefecture: addressData.prefecture,
				city: addressData.city,
				address: fullAddress,
			})
		);
	};

	const sortedVenueHistory = useMemo(
		() => [...venueHistory].sort((a, b) => b.lastUsedAt - a.lastUsedAt),
		[venueHistory]
	);

	const handleHistorySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value;
		setSelectedHistoryId(value);

		if (value === DEFAULT_OPTION_VALUE) {
			onChange(createEmptyVenueData());
			return;
		}

		const selectedVenue = sortedVenueHistory.find((item) => item.id === value);
		if (!selectedVenue) {
			return;
		}

		onChange({
			venueName: selectedVenue.venueName,
			postalCode: selectedVenue.postalCode,
			prefecture: selectedVenue.prefecture,
			city: selectedVenue.city,
			address: selectedVenue.address,
		});
	};

	const renderHistoryLabel = (history: VenueHistoryItem) => {
		const nameLabel = history.venueName?.trim();
		const addressLabel =
			`${history.prefecture}${history.city} ${history.address}`.trim();
		return nameLabel ? `${nameLabel}（${addressLabel}）` : addressLabel;
	};

	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
			<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
				会場情報
			</h3>
			<div className="space-y-4">
				{showHistorySelector && sortedVenueHistory.length > 0 && (
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							過去に入力した会場
						</label>
						<select
							value={selectedHistoryId}
							onChange={handleHistorySelect}
							className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
						>
							<option value={DEFAULT_OPTION_VALUE}>新しい会場を登録</option>
							{sortedVenueHistory.map((item) => (
								<option key={item.id} value={item.id}>
									{renderHistoryLabel(item)}
								</option>
							))}
						</select>
					</div>
				)}
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						会場名
						{required && (
							<span className="text-red-500 dark:text-red-400 ml-1">*</span>
						)}
					</label>
					<input
						type="text"
						name="venueName"
						value={venueData.venueName}
						onChange={handleVenueNameChange}
						required={required}
						placeholder="例：東京ドーム"
						className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
					/>
				</div>

				<AddressInput
					addressData={{
						postalCode: venueData.postalCode,
						prefecture: venueData.prefecture,
						city: venueData.city,
						streetAddress: venueData.address.split(" ")[0] || "",
						buildingName: venueData.address.split(" ")[1] || "",
					}}
					onChange={handleAddressChange}
					required={required}
				/>
			</div>
		</div>
	);
};

export default VenueInfo;
